import { QueryClientProvider, type QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, Outlet, useRouterState } from '@tanstack/react-router'
import { Toaster } from 'sonner'

import { isJwtExpired } from '@/lib/jwt'
import { queryClient } from '@/lib/query-client'
import { useAuthStore } from '@/shared/auth/stores/authStore'
import { ForceLogoutDialog } from '@/shared/components/feedback/ForceLogoutDialog'
import { AppNav } from '@/shared/components/layout/app-nav'
import { PageChrome } from '@/shared/components/layout/page-chrome'

interface RouterContext {
  queryClient: QueryClient
}

const PUBLIC_PATHS = ['/login']
// Routes that own the full content area (no 1240px container, no outer scroll).
const FULL_BLEED_PATHS = ['/gis']

function RootLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const showChrome = !PUBLIC_PATHS.some(
    (path) => pathname === path || pathname === `${path}/`,
  )
  const fullBleed = FULL_BLEED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  )

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster position="top-right" richColors closeButton />
      <ForceLogoutDialog />

      {showChrome && <PageChrome />}
      {showChrome && <AppNav />}

      <div
        className={
          showChrome ? 'relative z-10 flex h-screen flex-col overflow-hidden pt-[70px]' : ''
        }
      >
        <div className={showChrome ? (fullBleed ? 'min-h-0 flex-1' : 'flex-1 overflow-y-auto') : ''}>
          <div
            className={
              showChrome && !fullBleed
                ? 'mx-auto h-full w-full max-w-[1240px] px-6'
                : showChrome
                  ? 'h-full'
                  : ''
            }
          >
            <Outlet />
          </div>
        </div>
      </div>
    </QueryClientProvider>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Proactive dead-token check on boot + every navigation. Sets the
  // ForceLogout dialog rather than redirecting: a redirect to /login while
  // isAuthenticated is still true bounces back via login.tsx's inverse guard.
  beforeLoad: () => {
    const s = useAuthStore.getState()
    if (s.isAuthenticated && !s.forceLogout && (!s.accessToken || isJwtExpired(s.accessToken))) {
      s.setForceLogout(true)
    }
  },
  component: RootLayout,
  notFoundComponent: () => (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-2 px-4 text-center">
      <p className="text-4xl font-black text-gray-200">404</p>
      <h1 className="text-lg font-semibold text-gray-700">Page Not Found</h1>
      <p className="text-sm text-gray-400">The page you are looking for does not exist.</p>
    </div>
  ),
})
